import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Bookmark, Trash2, Search, Sparkles, BookOpen } from "lucide-react";
import { toast } from "sonner";
import ExportBriefingButton from "@/components/ExportBriefingButton";
import HighlightComments from "@/components/HighlightComments";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";

const HIGHLIGHTS_PAYLOAD = { include_highlights: true, include_forms: false };

export default function Highlights() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [items, setItems] = useState([]);
  const [team, setTeam] = useState([]);
  const [tab, setTab] = useState("mine");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/highlights").then((r) => setItems(r.data));
    if (isManager) api.get("/highlights/team").then((r) => setTeam(r.data));
  };
  useEffect(() => { load(); }, [isManager]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    try {
      await api.delete(`/highlights/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const source = tab === "team" ? team : items;
  const filtered = source.filter((h) =>
    !q || h.text.toLowerCase().includes(q.toLowerCase()) || h.source_title.toLowerCase().includes(q.toLowerCase()) || (h.owner_name || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Your Library</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">Saved Highlights</h1>
          <p className="text-sm text-slate-600 mt-1">Every passage you've saved — with its source and citation.</p>
        </div>
        <ExportBriefingButton filePrefix="highlights" payload={HIGHLIGHTS_PAYLOAD} label="Export highlights as PDF" testid="export-highlights-btn" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isManager && (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="highlights-tabs">
              <TabsTrigger value="mine" data-testid="highlights-tab-mine">Mine ({items.length})</TabsTrigger>
              <TabsTrigger value="team" data-testid="highlights-tab-team" title="Highlights other people intentionally shared with you via @mention. Everyone's other highlights and notes stay private to their owner.">Shared with me ({team.length})</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <Input data-testid="highlights-search" placeholder={tab === "team" ? "Search text, source, or team member…" : "Search your highlights…"} value={q} onChange={(e) => setQ(e.target.value)} className="pl-9"/>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map((h) => (
          <Card key={h.id} data-testid={`highlight-${h.id}`} className="p-5 bg-white border-slate-200">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#FDECD0] flex items-center justify-center flex-shrink-0">
                {h.source_type === "chat" ? <Sparkles className="w-4 h-4 text-[#7C3F00]"/> : <BookOpen className="w-4 h-4 text-[#7C3F00]"/>}
              </div>
              <div className="flex-1 min-w-0">
                <blockquote className="highlight-mark font-serif text-[#0A2A5E] leading-relaxed inline">
                  {h.text}
                </blockquote>
                {h.note && <p className="mt-2 text-sm text-slate-600 italic">Note: {h.note}</p>}
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  {tab === "team" && <span data-testid={`owner-${h.id}`} className="text-xs px-2 py-0.5 rounded-full bg-[#0A2A5E] text-white font-semibold">{h.owner_name} · shared with you</span>}
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#C7761A]">{h.source_type}</span>
                  <span className="text-xs text-slate-700 font-semibold">{h.source_title}</span>
                  {h.citation && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full">{h.citation}</span>}
                  <span className="text-xs text-slate-400 ml-auto">{new Date(h.created_at).toLocaleDateString()}</span>
                  {h.user_id === user?.id && (
                    <Button data-testid={`delete-${h.id}`} variant="ghost" size="sm" onClick={() => del(h.id)} className="text-slate-500 hover:text-red-600">
                      <Trash2 className="w-4 h-4"/>
                    </Button>
                  )}
                </div>
                <HighlightComments highlight={h} onChanged={load} />
              </div>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Bookmark className="w-10 h-10 mx-auto mb-3 opacity-40"/>
            <p className="font-medium">No highlights yet.</p>
            <p className="text-sm mt-1">Select any passage in the chat or a document to save it here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
